import { AccountResponseDto, CreateEnrollmentsByRequest, ProductResponseDto } from '@repo/types';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { HiX } from 'react-icons/hi';
import { grantEnrollments } from '@/api/enrollmentApi';
import { ProductSelectModal, Button, AccountSelectModal } from '@/components/ui';
import Toast, { type ToastType } from '@/components/ui/Toast';
import { queryClient } from '@/lib/queryClient';

interface EnrollmentGrantModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function EnrollmentGrantModal({
  isOpen,

  onClose,
}: EnrollmentGrantModalProps) {
  const [selectedProduct, setSelectedProduct] = useState<ProductResponseDto | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<AccountResponseDto | null>(null);
  const [isProductSelectModalOpen, setIsProductSelectModalOpen] = useState(false);
  const [isAccountSelectModalOpen, setIsAccountSelectModalOpen] = useState(false);

  const [toast, setToast] = useState<{
    isVisible: boolean;
    message: string;
    type: ToastType;
  }>({
    isVisible: false,
    message: '',
    type: 'success',
  });

  const grantEnrollmentsMutation = useMutation({
    mutationFn: async (payload: CreateEnrollmentsByRequest) => {
      await grantEnrollments(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      setToast({
        isVisible: true,
        message: '수강권 부여가 완료되었습니다.',
        type: 'success',
      });
      onClose();
    },
    onError: (error) => {
      console.error('❌ 수강권 부여 실패:', error);
      setToast({
        isVisible: true,
        message: '수강권 부여 중 오류가 발생했습니다.',
        type: 'error',
      });
    },
  });

  const handleProductSelect = (product: ProductResponseDto) => {
    setSelectedProduct(product);
  };
  const handleAccountSelect = (account: AccountResponseDto) => {
    setSelectedAccount(account);
  };

  // const { data: targetData } = useGetTargetCourseAndAccountIds({
  //   filterType,
  //   search,
  //   startDate,
  //   endDate: endDate || undefined,
  //   targetProductId: selectedProduct?.id ?? 0,
  // });

  const handleSubmit = async () => {
    if (!selectedProduct) {
      alert('상품을 선택해주세요.');
      return;
    }
    // if (!targetData || targetData.length === 0) {
    //   alert('부여 대상이 없습니다.');
    //   return;
    // }

    await grantEnrollmentsMutation.mutateAsync({ productId: selectedProduct.id, accountId: selectedAccount.id });
  };

  const handleClose = () => {
    setSelectedProduct(null);
    setSelectedAccount(null);
    onClose();
  };

  if (!isOpen && !toast.isVisible) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl">
          {/* 헤더 */}
          <div className="flex items-center justify-between border-b border-slate-200 p-6">
            <h2 className="text-xl font-semibold text-slate-800">유료특강 수강권 부여</h2>
            <button onClick={handleClose} className="rounded-lg p-1 transition-colors hover:bg-slate-100">
              <HiX className="h-5 w-5 text-slate-500" />
            </button>
          </div>

          {/* 내용 */}
          <div className="p-6">
            <div className="space-y-6">
              {/* 상품 선택 */}
              <div>
                <label className="block text-sm font-medium text-slate-700">부여할 상품</label>
                {selectedProduct ? (
                  <div className="mt-2 flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4">
                    <div>
                      <p className="font-medium text-slate-900">{selectedProduct.title}</p>
                      <p className="mt-1 text-sm text-slate-500">상품 ID: #{selectedProduct.id}</p>
                      <p className="mt-1 text-sm text-slate-500">상품 slug: {selectedProduct.slug}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setIsProductSelectModalOpen(true)}>
                      변경
                    </Button>
                  </div>
                ) : (
                  <div className="mt-2">
                    <Button variant="outline" onClick={() => setIsProductSelectModalOpen(true)} className="w-full">
                      상품 선택하기
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">부여할 계정</label>
                {selectedAccount ? (
                  <div className="mt-2 flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4">
                    <div>
                      <p className="font-medium text-slate-900">{selectedAccount.name}</p>
                      <p className="mt-1 text-sm text-slate-500">계정 ID: #{selectedAccount.id}</p>
                      <p className="mt-1 text-sm text-slate-500">계정 email: {selectedAccount.email}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setIsAccountSelectModalOpen(true)}>
                      변경
                    </Button>
                  </div>
                ) : (
                  <div className="mt-2">
                    <Button variant="outline" onClick={() => setIsAccountSelectModalOpen(true)} className="w-full">
                      계정 선택하기
                    </Button>
                  </div>
                )}
              </div>
              {/* <div>
                <label className="block text-sm font-medium text-slate-700">대상 코스/인원</label>
                <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  {selectedProduct ? (
                    targetData && targetData.length > 0 ? (
                      <div>
                        {targetData.map((item) => (
                          <div key={item.courseId} className="text-lg font-semibold text-slate-900">
                            <p>{item.accountIds.length}명</p>
                            <p className="text-sm text-slate-500">대상 코스 Id: #{item.courseId}</p>
                            <p className="text-sm text-slate-500">
                              대상 인원 Id: {item.accountIds.map((id) => `#${id}`).join(', ')}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-lg font-semibold text-slate-900">가능한 대상 코스/인원이 없습니다.</p>
                    )
                  ) : (
                    <p className="text-lg font-semibold text-slate-900">상품을 선택해주세요.</p>
                  )}
                  <p className="mt-1 text-sm text-slate-500">필터링된 사용자에게 수강권이 부여됩니다.</p>
                </div>
              </div> */}
            </div>
          </div>

          {/* 푸터 */}
          <div className="flex justify-end gap-3 border-t border-slate-200 p-6">
            <Button variant="outline" onClick={handleClose}>
              취소
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={!selectedProduct}>
              수강권 부여
            </Button>
          </div>
        </div>
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast((prev) => ({ ...prev, isVisible: false }))}
      />

      {/* Nested Modal: 상품 선택 모달 */}
      <ProductSelectModal
        isOpen={isProductSelectModalOpen}
        onClose={() => setIsProductSelectModalOpen(false)}
        onSelect={handleProductSelect}
      />
      {/* Nested Modal: 계정 선택 모달 */}
      <AccountSelectModal
        isOpen={isAccountSelectModalOpen}
        onClose={() => setIsAccountSelectModalOpen(false)}
        onSelect={handleAccountSelect}
      />
    </>
  );
}
